import React from 'react';
import {
  CommunicationAttachment,
  DocumentReferenceAttachment,
  ObservationAttachment,
  PAGE_NUMBER_EXTENSION_URL,
} from '../AttachmentArbiter/Attachment';
import { ImpressionItem } from '../../ExecutiveSummary/executive-summary';
import { AttachmentItem } from './AttachmentList';
import { useAttachmentArbiter } from '../AttachmentArbiter';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { DocumentReferenceContent } from '../AttachmentArbiter/modals/DocumentReferenceModal';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { defineMessages, useIntl } from 'react-intl';
import { ExternalLink } from '@lifeomic/chromicons';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { Tooltip } from '@lifeomic/chroma-react/components/Tooltip';
import { lighten } from '@mui/material/styles';
import { Sparkline } from '../../Measurements/Graphs/Ranges/Sparkline';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { useObservationRenderingTracking } from '../ImpressionEditor/ObservationRenderingTrackingProvider';
import { MedicalResultChartCondensed } from '../../Measurements/Graphs/Ranges/MedicalResultsChartCondensed/MedicalResultChartCondensed';
import { footnoteRegexp } from '../../ExecutiveSummary/utils';
import { useLocalStorageState } from '../../../../../hooks';

export type ViewMode =
  | 'editing'
  | 'compact'
  | 'expanded'
  | 'presentation-expanded'
  | 'presentation-compact'
  | 'print';

/**
 * For compact and expanded view modes, we want to move the footnote references to the end of the block (i.e., double-newlines)
 * so that we can render them as widgets between paragraphs or between lists, but not between list items.
 * The regexp matches both the generated footnotes and the footnote references that get edited by the edit mode and
 * attachment arbiter.
 */

const moveRegexpToEndOfBlock = (text: string, regexp: RegExp): string => {
  return text
    .split('\n\n')
    .map(line => {
      if (line.trim().endsWith(':')) {
        return line;
      }
      const match = line.match(regexp);
      if (match) {
        // when we "collect" the footnotes at the end of the line, we also want to deduplicate them
        const moved = new Set<string>();
        match.forEach(footnote => {
          line = line.replace(footnote, '').trimEnd();
          moved.add(footnote.trim());
        });
        return line + '\n' + Array.from(moved).join(' ');
      }
      return line;
    })
    .join('\n\n');
};

export const getSummaryForViewMode = (
  summary: string,
  _viewMode: ViewMode,
): string => {
  const moved = moveRegexpToEndOfBlock(summary, footnoteRegexp);
  return moved.replace(/\n\n\n/g, '\n\n');
};

const useStyles = makeStyles(theme => ({
  footnote: {
    minWidth: 0,
    paddingLeft: 0,
    paddingRight: 0,
    fontWeight: 400,
    cursor: 'pointer',
    fontSize: theme.pxToRem(13),
    lineHeight: 0,
    verticalAlign: 'super',
    'a&': {
      textDecoration: 'none',
      '&:hover, &:focus': {
        textDecoration: 'underline',
      },
    },
  },
  border: {
    border: `1px solid ${theme.palette.divider}`,
  },
  attachment: {
    display: 'inline-block',

    '&:before': {
      content: '.',
      display: 'block',
    },
  },
  expansionPanel: {
    borderColor: theme.palette.divider,
    border: '1px solid',
    borderRadius: theme.pxToRem(4),
    overflow: 'hidden',
    margin: theme.spacing(1.5, 0),
    '& div': {
      boxShadow: 'none',
      background: lighten(theme.palette.graphite[900], 0.9),
    },
    '& button': {
      backgroundColor: 'transparent',
    },
    '@media print': {
      '&': {
        breakInside: 'avoid',
      },
    },
  },
  expansionPanelContent: {
    padding: 0,
    height: 600,
  },
  noMaxHeightContent: {
    maxHeight: 'unset !important',
  },
  smallExpansionPanelContent: {
    padding: 0,
    height: 'auto',
    backgroundColor: 'white',
  },
  externalLink: {
    paddingLeft: 0,
    paddingRight: 0,
  },
}));

const messages = defineMessages({
  documentReferenceAttachment: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.inlineAttachment.documentReferenceAttachment',
    defaultMessage: '{display} (Page {pageNumber})',
  },
  openInNewTab: {
    id: 'phc-web-toolkit.subjectViewer.executiveSummary.inlineAttachment.openInNewTab',
    defaultMessage: 'Open in new tab',
  },
});

export const InlineAttachment = ({
  footnoteId,

  patient,
  isEditing,
  viewMode,
  impression,
  onDelete,
}: {
  footnoteId: string;
  text: string;
  patient: fhir.Patient;
  isEditing: boolean;
  viewMode: ViewMode;
  impression: ImpressionItem;
  onDelete: () => void;
}) => {
  const [cachedExpansionState, setCachedExpansionState] = useLocalStorageState<
    'expanded' | 'collapsed'
  >(
    `es-inline-attachment-expansion-${patient.id}-${impression.impressionType}-${footnoteId}`,
    viewMode === 'presentation-compact' ? 'collapsed' : 'expanded',
  );
  const { renderedObservations } = useObservationRenderingTracking();
  const effectiveViewMode = isEditing ? 'editing' : viewMode;
  const { attachmentMap, openAttachment } = useAttachmentArbiter();
  const attachment =
    impression.getDeduplicatedAttachmentByIdentifier(footnoteId);
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const isPrint = viewMode === 'print';
  if (!attachment) {
    return null;
  }

  const attachmentReference = attachmentMap[attachment.reference || ''];
  if (!attachmentReference) {
    return null;
  }

  if (
    impression.impressionType === 'synopsis' &&
    attachmentReference instanceof ObservationAttachment
  ) {
    return (
      <ObservationAttachmentInSparkline
        attachment={attachmentReference}
        attachmentReference={attachment}
        patient={patient}
        summaryType={impression.impressionType}
      />
    );
  }

  if (effectiveViewMode === 'editing') {
    return (
      <Box direction="row" marginTop={2} className={classes.attachment}>
        <AttachmentItem
          attachment={attachment!}
          disableDeepLink={false}
          disableThumbnail={false}
          className={classes.border}
          action="edit"
          onUseAttachment={onDelete}
        />
      </Box>
    );
  }

  if (attachmentReference instanceof CommunicationAttachment) {
    // Note: we don't render communications in the executive summary anymore
    // based on physician feedback (they don't want them in the AR because they're
    // not member-formatted/facing. They're good _citations_ but not attachments.)
    return null;
  }

  if (attachmentReference instanceof ObservationAttachment) {
    if (attachmentReference.primaryCode && attachmentReference.isBiomarker) {
      const shouldRender = renderedObservations.shouldRenderAttachment(
        attachmentReference,
        impression.impressionType,
      );

      if (!shouldRender) {
        return null;
      }

      return (
        <MedicalResultChartCondensed
          dimension={{
            codes: [attachmentReference.primaryCode],
          }}
          patient={patient}
          source={undefined} // todo: support source when AR might reference wearables data, or allow some kind of "any" source from history endpoint
        />
      );
    }
  }
  if (attachmentReference instanceof DocumentReferenceAttachment) {
    if (effectiveViewMode === 'compact' || effectiveViewMode === 'print') {
      return (
        <Box direction="row" marginTop={2} className={classes.attachment}>
          <AttachmentItem
            attachment={attachment!}
            disableDeepLink={false}
            disableThumbnail={false}
            className={classes.border}
            action="readonly"
          />
        </Box>
      );
    }
    if (
      effectiveViewMode === 'expanded' ||
      effectiveViewMode === 'presentation-expanded' ||
      effectiveViewMode === 'presentation-compact'
    ) {
      const pageNumber =
        attachment?.extension?.find(
          ext => ext.url === PAGE_NUMBER_EXTENSION_URL,
        )?.valueInteger ?? 1;
      const attachmentData = attachmentMap[attachment.reference || ''];
      const title = formatMessage(messages.documentReferenceAttachment, {
        display: attachmentData.display,
        pageNumber,
      });

      const content = (
        <DocumentReferenceContent
          attachment={attachmentReference}
          reference={attachment}
          disablePdfNav={true}
        />
      );

      if (attachmentReference.isImage()) {
        return content;
      }

      return (
        <ExpansionPanel
          title={title}
          isOpen={cachedExpansionState === 'expanded' || isPrint}
          onToggle={isExpanded => {
            setCachedExpansionState(isExpanded ? 'expanded' : 'collapsed');
          }}
          className={classes.expansionPanel}
          innerContentClassName={classes.expansionPanelContent}
          contentClassName={isPrint ? classes.noMaxHeightContent : ''}
          secondaryHeader={
            <Tooltip title={formatMessage(messages.openInNewTab)}>
              <IconButton
                data-testid="open-document-reference-button"
                aria-label={formatMessage(messages.openInNewTab)}
                className={classes.externalLink}
                icon={ExternalLink}
                size={0}
                onClick={() =>
                  openAttachment(attachment, { disableDeepLink: false })
                }
              />
            </Tooltip>
          }
        >
          {content}
        </ExpansionPanel>
      );
    }
  }

  console.warn('unsupported inline attachment type', attachmentReference);
  return null;
};

function ObservationAttachmentInSparkline({
  attachment,
  attachmentReference,
  patient,
  summaryType,
}: {
  attachment: ObservationAttachment;
  attachmentReference: fhir.Reference;
  patient: fhir.Patient;
  summaryType: string;
}) {
  const { openAttachment } = useAttachmentArbiter();
  const { renderedObservations } = useObservationRenderingTracking();

  const shouldRender = renderedObservations.shouldRenderAttachment(
    attachment,
    summaryType,
  );

  if (!shouldRender || !attachment.primaryCode) {
    return null;
  }

  return (
    <ButtonUnstyled
      onClick={() => openAttachment(attachmentReference)}
      role="button"
      tabIndex={0}
      title={attachment.display || ''}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          openAttachment(attachmentReference);
        }
      }}
    >
      <Sparkline
        dimension={{
          codes: [attachment.primaryCode],
        }}
        patient={patient}
      />
    </ButtonUnstyled>
  );
}

const MemoizedInlineAttachment = React.memo(InlineAttachment);

export default MemoizedInlineAttachment;
